import { APEX_API_URL } from '@/constants';
import { getHeaderWithToken } from '@/helpers';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { IGetCartProduct } from '@/interfaces/cart';
import { IGuestCheckOut } from '@/interfaces/checkout';
import { IBopisOrderStatus, IEmiInfo, IOnlinePaymentResponse, IOrderDetails, IOrders, IPaymentType, IPlaceOrderRequest, IPlaceOrderResponse, IPurchaseHistory, IRequests, IReturnExchangeInitialization, IReturnExchangeRequestDetails, IReturnExchangeSubmit, ISwitchOrderRequest } from '@/interfaces/orders';
import { getAxiosInstance } from '@/utils/axios-instance';

export const placeOrder = async (requestBody: IPlaceOrderRequest, session?: INextAuthSessionModel | null): Promise<IPlaceOrderResponse> => {
  const response = await getAxiosInstance().post(`${APEX_API_URL}/api/checkout/place-order`, requestBody, getHeaderWithToken(session));
  return response.data;
};


export const getPaymentType = async (
  session?: INextAuthSessionModel | null,
  emiInformation?: {
    bankId: string;
    bankEmiId: string;
  },
  isBopis?: boolean | null,
  isExpress?: boolean | null,
  discountId?: string,
): Promise<IPaymentType[]> => {
  const params = new URLSearchParams();

  if (emiInformation?.bankId) params.append('bankId', emiInformation.bankId);
  if (emiInformation?.bankEmiId) params.append('bankEmiId', emiInformation.bankEmiId);
  if (isBopis !== undefined && isBopis !== null) params.append('isBopis', String(isBopis));
  if (isExpress !== undefined && isExpress !== null) params.append('isExpress', String(isExpress));
  if (discountId) params.append('discountId', discountId);

  const response = await getAxiosInstance().get(
    `${APEX_API_URL}/api/payment/options${params.toString() ? `?${params.toString()}` : ''}`,
    getHeaderWithToken(session),
  );

  return response.data;
};

export const switchOrder = async (requestBody: ISwitchOrderRequest, session?: INextAuthSessionModel | null): Promise<any> => {
  const response = await getAxiosInstance().patch(`${APEX_API_URL}/api/order/switch-delivery`, requestBody, getHeaderWithToken(session));
  return response.data;
};

export const makePaymentViaOption = async (
  orderCode: string,
  paymentOptionId: string,
  session?: INextAuthSessionModel | null,
): Promise<{ amount: number; paymentOptionType: number }> => {
  const response = await getAxiosInstance().post(
    `${APEX_API_URL}/api/payment/pay/option`,
    { orderCode, paymentOptionId },
    getHeaderWithToken(session),
  );
  return response.data;
};

export const makeOnlinePayment = async (
  orderCode: string,
  amount: number,
  redirectUrl: string,
  gatewayId: string,
  paymentOptionId: string,
  session?: INextAuthSessionModel | null,
): Promise<IOnlinePaymentResponse> => {
  const response = await getAxiosInstance().post(
    `${APEX_API_URL}/api/payment/pay/gateway`,
    { orderCode, amount, redirectUrl, gatewayId, paymentOptionId },
    getHeaderWithToken(session),
  );
  return response.data;
};

export const getOrderList = async (
  session?: INextAuthSessionModel | null,
  pageIndex?: number | null,
  pageSize?: number | null,
  orderStatus?: number | null,
  startDate?: string | null,
  endDate?: string | null,
): Promise<IOrders> => {
  const response = await getAxiosInstance().get(`${APEX_API_URL}/api/order/list`, {
    params: {
      pageIndex,
      pageSize,
      orderStatus,
      startDate,
      endDate,
    },
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });
  return response.data;
};

export const getPurchaseHistory = async (
  session?: INextAuthSessionModel | null,
  pageNumber?: number | null,
  pageSize?: number | null,
): Promise<IPurchaseHistory> => {
  const response = await getAxiosInstance().get(`${APEX_API_URL}/api/order/purchase-history`, {
    params: {
      pageNumber,
      pageSize,
    },
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });
  return response.data;
};

export const getReturnExchangeList = async (
  session?: INextAuthSessionModel | null,
  pageSize?: number | null,
  pageIndex?: number | null,
  requestType?: number
): Promise<IRequests> => {
  const response = await getAxiosInstance().get(`${APEX_API_URL}/api/return-exchange/list`, {
    params: {
      pageIndex,
      pageSize,
      requestType
    },
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });
  return response.data;
};

export const getOrderDetails = async (
  orderCode: string,
  session?: INextAuthSessionModel | null,
  isSwitchToHomeDelivery: boolean = false,
  shippingAddressId?: string,
  billingAddressId?: string,
): Promise<IOrderDetails> => {
  const response = await getAxiosInstance().get(
    `${APEX_API_URL}/api/order/detail?orderCode=${orderCode}&isSwitchToHomeDelivery=${isSwitchToHomeDelivery}&shippingAddressId=${shippingAddressId}&billingAddressId=${billingAddressId}`,
    getHeaderWithToken(session),
  );
  return response.data;
};

export const initializeReturnExchange = async (
  orderCode: string,
  requestType: number,
  session?: INextAuthSessionModel | null,
): Promise<IReturnExchangeInitialization> => {
  const response = await getAxiosInstance().get(
    `${APEX_API_URL}/api/return-exchange/initialize?OrderCode=${orderCode}&RequestType=${requestType}`,
    getHeaderWithToken(session),
  );
  return response.data;
};

export const requestReturnExchange = async (
  requestCode: string,
  session?: INextAuthSessionModel | null,
): Promise<IReturnExchangeRequestDetails> => {
  const response = await getAxiosInstance().get(
    `${APEX_API_URL}/api/return-exchange/${requestCode}`,
    getHeaderWithToken(session),
  );
  return response.data;
};

export const submitReturnExchange = async (
  formData: FormData,
  onUploadProgress?: (loaded: number, total: number) => void,
): Promise<IReturnExchangeSubmit> => {
  const response = await getAxiosInstance().post(
    `${APEX_API_URL}/api/return-exchange/submit`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (event.total && onUploadProgress) {
          onUploadProgress(event.loaded, event.total);
        }
      },
    }
  );
  return response.data;
};

export const getOrderSummery = async (
  OrderDeliveryType: number | undefined,
  ShippingAddressId?: string,
  StoreIds?: string[],
  session?: INextAuthSessionModel | null,
  storeId?: string,
  PointsToRedeem?: number | null,
  coupon?: string,
  PhoneNumber?: string,
  BankId?: string,
  BankEmiId?: string,
  corporateDiscountOtp?: string,
  removeCorporateDiscount?: boolean,
  CouponId?: string,
  isObjectivebasedCampaignClaimed?: boolean,
): Promise<IGetCartProduct> => {
  const params = new URLSearchParams();

  params.append('OrderDeliveryType', String(OrderDeliveryType));
  if (ShippingAddressId) params.append('ShippingAddressId', ShippingAddressId);

  StoreIds?.forEach((id) => {
    params.append('StoreIds', id);
  });
  
  if (storeId) params.append('storeId', storeId);
  if (PointsToRedeem) params.append('PointsToRedeem', String(PointsToRedeem));
  if (coupon) params.append('coupon', coupon);
  if (CouponId) params.append('CouponId', CouponId);
  if (PhoneNumber) params.append('PhoneNumber', PhoneNumber);
  if (BankId) params.append('BankId', BankId);
  if (BankEmiId) params.append('BankEmiId', BankEmiId);
  if (corporateDiscountOtp) params.append('Otp', corporateDiscountOtp);
  if (removeCorporateDiscount) params.append('RemoveCorporateDiscount', String(removeCorporateDiscount));
  if (isObjectivebasedCampaignClaimed) params.append('IsObjectivebasedCampaignClaimed', String(isObjectivebasedCampaignClaimed));


  const paramsString = params.toString();

  const response = await getAxiosInstance().get(`${APEX_API_URL}/api/checkout/order-summary` + (paramsString ? `?${paramsString}` : ''), {
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });

  return response.data;
};
export const guestOrder = async (payload: IGuestCheckOut): Promise<any> => {
  const response = await getAxiosInstance().post(`${APEX_API_URL}/api/checkout/guest-checkout`, payload);
  return response.data;
};
export const giftVoucher = async (VoucherCoupon?: string, session?: INextAuthSessionModel | null): Promise<any> => {
  const response = await getAxiosInstance().get(`${APEX_API_URL}/api/checkout/gift-voucher-amount`, {
    params: {
      VoucherCoupon,
    },
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });
  return response.data;
};

export const reOrder = async (orderCode: string): Promise<any> => {
  const payload = {
    orderCode,
  };
  const response = await getAxiosInstance().post(`${APEX_API_URL}/api/checkout/re-order`, payload);
  return response.data;
};

export const cancelReturnExchange = async (
  requestCode: string,
  orderItemIds: string[],
  cancellationReason: string
): Promise<any> => {
  const payload = {
    requestCode,
    orderItemIds,
    cancellationReason,
  };

  const response = await getAxiosInstance().post(
    `${APEX_API_URL}/api/return-exchange/cancel`,
    payload
  );

  return response.data;
};

export const getEMIInfo = async (): Promise<IEmiInfo[]> => {
  let url = `${APEX_API_URL}/api/payment/bank`;
  const response = await getAxiosInstance().get(url);
  return response.data;
};

export const sendCorporateDiscountOtp = async (session?: INextAuthSessionModel | null): Promise<number> => {
  const response = await getAxiosInstance().post(
    `${APEX_API_URL}/api/checkout/send-corporate-discount-otp`,
    {}, // empty body
    getHeaderWithToken(session),
  );
  return response.data;
};

export const getBopisOrderStatus = async (session?: INextAuthSessionModel | null): Promise<IBopisOrderStatus[]> => {
  const response = await getAxiosInstance().get(`${APEX_API_URL}/api/OrderStatus`, {
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });
  return response.data;
};
